// Pure derivations for listing card display — no Vue, no store. Takes a CardListing, the
// already-resolved single-MLS compliance entry ({ name, rule } | null) for that listing's mls.id
// (callers look it up from the deduped compliance map before calling), and cdnUrl.

function timeOnMarket(listing) {
    const base = listing.createdAt || listing.listDate;
    const t = base && new Date(base.replace(' ', 'T') + 'Z');
    if (!t || isNaN(t.getTime())) return '';
    const s = Math.floor((Date.now() - t.getTime()) / 1000);
    if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} Min`;
    if (s <= 172800) {
        const h = Math.floor(s / 3600);
        return `${h} ${h === 1 ? 'Hour' : 'Hours'}`;
    }
    return `${Math.floor(s / 86400)} Days`;
}

function soldLabel(date) {
    if (!date) return '';
    const d = new Date(date.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return '';
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    if (d < cutoff) return '';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
}

function openLabel(oh) {
    if (!oh?.date) return '';
    const d = new Date(`${oh.date}T00:00:00`);
    if (isNaN(d.getTime())) return '';
    const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const week = DAYS[d.getDay()];
    if (!oh.startTime || !oh.endTime) return week;
    const hr = (t) => { const h = parseInt(t); return h > 12 ? h - 12 : h; };
    return `${week} ${hr(oh.startTime)}-${hr(oh.endTime)}`;
}

export function mapCardsResult(cardsResult, cdnUrl) {
    return (cardsResult?.listings || []).map((item) => mapCardListing(item, cardsResult.compliance?.[item.mls?.id] || null, cdnUrl));
}

export function mapCardListing(listing, compliance, cdnUrl) {
    const status = listing.listingStatus?.name || '';
    const listingTypeName = listing.listingType?.name;
    const isLand = listingTypeName === 'LAND';
    const isRentals = status === 'FOR_RENT';
    const isSold = status === 'SOLD';
    const isComingSoon = listing.listingStatus?.displayName === 'Coming Soon';
    const dom = listing.daysOnMarket;
    const rules = compliance?.rule ?? [];
    const isNew = ['ACTIVE', 'FOR_RENT'].includes(status) && !isComingSoon && typeof dom === 'number' && dom >= 0 && dom <= 7 && !rules.includes('hideNew');
    const price = listing.closePrice || listing.listPrice || 0;
    const openDate = listing.currentOpenHouses?.[0] || null;
    const createTimeDisplay = isNew && listing.listingCompliance?.showOnMarketTime ? timeOnMarket(listing) : '';

    const a = listing.address || {};
    const addressLine = a.addressInfo || 'Undisclosed';

    const { stdThumbnail, imageDownloaderStatus, imageFormat } = listing;
    const tnImgPath = !stdThumbnail
        ? cdnUrl + 'images/nophoto_small.svg'
        : imageDownloaderStatus === 7
        ? stdThumbnail.replace(/_t\.(jpeg|webp)$/, `_p.${imageFormat === 'webp' ? 'webp' : 'jpeg'}`)
        : stdThumbnail;

    let formatMlsInfo = '';
    if (rules.includes('showAgent') || rules.includes('showBroker')) {
        let text = '';
        if (rules.includes('showAgent') && listing.agentListFullName) text += ' ' + listing.agentListFullName;
        if (rules.includes('showBroker')) {
            const office = listing.officeListName || compliance.name;
            if (office) text += `${text ? ',' : ''} ${office}`;
        }
        if (rules.includes('showBrokerPhone') && listing.officeListPhone) text += `${text ? ',' : ''} Phone #${listing.officeListPhone}`;
        if (rules.includes('showMLSNum')) text += ' MLS#' + listing.mlsNumber;
        if (text) formatMlsInfo = 'Listed by ' + text;
    }

    const mlsId = listing.mls?.id;
    const mlsLogoSrc =
        mlsId && rules.includes('showMLSLogo') && !rules.includes('hidePropertyCardLogo')
            ? cdnUrl + 'images/desktop/logo/logo_' + mlsId + '.png'
            : '';

    const dppUrl = listing.listingUrl ? `/${listing.listingUrl.replace(/^\//, '')}` : '#';

    return {
        propertyId: listing.mlsPublicRecordAssociation?.id || '',
        listingId: listing.id,
        dppUrl,
        path: dppUrl,
        geo: {
            address: addressLine,
            city: a.city || '',
            state: a.state || '',
            zipcode: a.zipCode || '',
            lat: a.latitude ?? 0,
            lng: a.longitude ?? 0
        },
        houseRealStatus: status,
        houseRealStatusDisplay: isSold ? 'SOLD' : status === 'PENDING' ? 'Pending' : status === 'REMOVED' ? 'Delisted' : status === 'OFF_MARKET' ? 'Off Market' : status === 'FOR_RENT' ? 'For Rent' : '',
        propertyType: listingTypeName || null,
        daysOnMovoto: dom ?? 0,
        openHouses: listing.currentOpenHouses || [],
        tnImgPath,
        photoCount: listing.photoCount ?? 0,
        price,
        bed: listing.bedrooms ?? 0,
        bath: listing.bathroomsTotal ?? 0,
        area: (isLand ? listing.lotSizeSqft : listing.sqftTotal) || 0,
        isLand,
        isRentals,
        isSold,
        isNew,
        isPriceReduced: listing.priceChangeAmount < 0,
        showPriceChange: !!listing.priceChangeAmount && status === 'ACTIVE' && !isNew && !isComingSoon,
        priceChange: listing.priceChangeAmount ?? 0,
        openDate,
        openDateV1: openLabel(openDate),
        soldDate: listing.soldDate ?? null,
        showSoldDate: !!soldLabel(listing.soldDate),
        createTimeDisplay,
        formatMlsInfo,
        mlsLogoSrc,
        mlsName: compliance?.name || '',
    };
}
